<template>
  <ion-page>
    <ion-content class="h-full">
      <round-header
        image-type="full"
        :image="course.image"
        :title="course.name"
        disable-edit-mode="true"
        back="/courses"
      ></round-header>
      <div id="container">
        <div class="flex flex-wrap justify-center">
          <loader v-if="!loaded" class="h-16 w-16 animate-spin text-[#00000033]" />
          <template v-for="(lesson, i) in course.lessons" v-else :key="lesson.lessons_id.id">
            <course-button
              :image="lesson.lessons_id.image"
              :icon="isLessonFinished(lesson.lessons_id.id) ? CheckCircle : null"
              :icon-class="'bg-[#96bf5d99] text-white p-14'"
              class="m-4 bg-gradient-to-t from-blue-300 to-blue-100"
              :class="isLessonUnlocked(i) ? 'opacity-100' : 'pointer-events-none opacity-25'"
              :label="lesson.lessons_id.name"
              :sublabel="
                !isLessonUnlocked(i) && lessonUnlockTime(i)
                  ? `Unlocks in ${lessonUnlockTime(i)}`
                  : undefined
              "
              @click="router.push('/courses/' + course.id + '/lesson/' + lesson.lessons_id.id)"
            />
          </template>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts" async>
import { IonContent, IonPage, onIonViewWillEnter } from '@ionic/vue';
import RoundHeader from '../../components/RoundHeader.vue';
import { directus } from '../../services/Directus.ts';
import { readItem } from '@directus/sdk';
import { onMounted, ref, type Ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import router from '../../router';
import CourseButton from '../../components/CourseButton.vue';
import { useCurrentUser } from 'vuefire';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { CheckCircle, Loader } from 'lucide-vue-next';

const route = useRoute();
const courseId = route.params.courseId as string;
const course: Ref<any> = ref({});

const user = useCurrentUser();
const db = getFirestore();
const progress: Ref<any> = ref({});
const loaded: Ref<boolean> = ref(false);

function isLessonFinished(id: number): boolean {
  return progress.value.lessons?.[id]?.completed ?? false;
}

function isLessonUnlocked(index: number): boolean {
  if (index == 0) return true;
  const prevLessonId = course.value.lessons[index - 1].lessons_id.id;
  if (!isLessonFinished(prevLessonId)) return false;

  const commitment = progress.value.commitments?.[prevLessonId];
  if (commitment && commitment.fulfilled === null) {
    return new Date() >= new Date(commitment.unlocksAt);
  }
  return true;
}

function lessonUnlockTime(index: number): string | null {
  if (index == 0) return null;
  const prevLessonId = course.value.lessons[index - 1].lessons_id.id;
  if (!isLessonFinished(prevLessonId)) return null;

  const commitment = progress.value.commitments?.[prevLessonId];
  if (!commitment || commitment.fulfilled !== null) return null;

  const diffMs = new Date(commitment.unlocksAt).getTime() - Date.now();
  if (diffMs <= 0) return null;

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

onMounted(async () => {
  course.value = await directus.request(
    readItem('courses', courseId, {
      fields: ['*', 'lessons.lessons_id.*'],
    })
  );
});

async function fetchData() {
  if (!user.value || !course.value) {
    return;
  }
  const docRef = doc(db, `users/${user.value.uid}/courses/${course.value.id}`);
  const document = await getDoc(docRef);
  progress.value = document?.data() ?? {};
  loaded.value = true;
}

watch(course, async () => {
  await fetchData();
});

watch(user, async () => {
  await fetchData();
});

onIonViewWillEnter(async () => {
  await fetchData();
});
</script>
